import { useMemo } from 'react';
import { GetManufacturers } from 'services/graphql/cms';
import { useManufacturers } from 'services/hooks/queries/useManufacturers';

export type ItemManufacture = GetManufacturers['manufacturers_connection']['nodes'][0];

export type AlphabetItem = {
  char: string[1];
  enabled: boolean;
};

const transformToGroupByFirstLetter = (manufacturers: ItemManufacture[]) => {
  const groups = new Map<string, ItemManufacture[]>();

  manufacturers.forEach((item) => {
    const firstChar = item.name[0].toUpperCase();
    const existingGroup = groups.get(firstChar);

    if (existingGroup) {
      groups.set(firstChar, [...existingGroup, item]);
    } else {
      groups.set(firstChar, [item]);
    }
  });

  return new Map([...groups.entries()].sort());
};

const useGroupedMakers = (nameQuery?: string) => {
  const { data: manufacturersData } = useManufacturers({ limit: 1000 });

  const filteredMakers = useMemo(() => {
    if (nameQuery && nameQuery.length > 1) {
      // eslint-disable-next-line arrow-body-style
      return manufacturersData?.manufacturers_connection?.nodes.filter((item) => {
        return item.name.toUpperCase().indexOf(nameQuery.toUpperCase()) > -1;
      });
    }

    return manufacturersData?.manufacturers_connection?.nodes;
  }, [manufacturersData?.manufacturers_connection?.nodes, nameQuery]);

  const allMakers = useMemo<Map<string, ItemManufacture[]>>(() => {
    const makers = transformToGroupByFirstLetter(filteredMakers);

    return makers;
  }, [filteredMakers]);

  const alphabet = useMemo<AlphabetItem[]>(() => {
    const al: AlphabetItem[] = [];

    for (let i = 0; i < 26; i += 1) {
      const char = (i + 10).toString(36).toUpperCase();

      const isEnabled = Array.from(allMakers.keys()).findIndex((item) => item === char) > -1;

      al.push({ char, enabled: isEnabled });
    }

    return al;
  }, [allMakers]);

  return {
    makers: allMakers,
    alphabet,
  };
};

export { useGroupedMakers };
